/**
 * The eval() boundary — the ONLY file in v2 containing `eval` (SPEC §2.1
 * rule, REQ-FORM-1).
 *
 * The engine is textual substitution + raw DIRECT eval. Direct eval is
 * scope-sensitive: a formula identifier that survives substitution resolves
 * against the lexical scope AT THE EVAL SITE (ERRATA #10 / FORM-010c). v1
 * had three eval sites (UpdatePrice, EvaluateFormulaElements' callback,
 * EvaluateCalculationElements' callback) with distinct scopes, so the
 * isolated boundary is one thin wrapper per site, each re-declaring — with
 * the same names and values — the locals a formula could capture there.
 *
 * Only ALL-LOWERCASE locals are reproduced: FormulaParser lowercases the
 * whole formula in pass 1 (REQ-FORM-2), so mixed-case locals
 * (formulaBeforeEval, evaluatedNewPrice, finalValue, parsedFormula,
 * evaluatedValue, elem_finalValue, element_parsedValue) are uncapturable.
 * Known negligible divergence: v1's module closure also exposed the
 * lowercase `debounce` function to all three sites; it is not reproduced
 * (only reachable by a formula literally naming `debounce`).
 */

/* eslint-disable @typescript-eslint/no-unused-vars */

/**
 * UpdatePrice's eval site (REQ-PRICE-1). Capturable locals: `formula` (the
 * parsed formula string itself) and the hoisted catch-var `msg` (undefined
 * at eval time) — pinned by FORM-010c.
 *
 * All scope shims here are PARAMETERS, never `var x; void x;` locals:
 * esbuild dead-code-eliminates unused locals (even in a direct-eval scope)
 * but always keeps parameters. Callers never pass the shim arguments in this
 * function (`msg` must be undefined, exactly like v1's hoisted catch-var).
 */
export function evaluateFormula(parsedFormula: any, msg?: any): any {
  var formula = parsedFormula;
  return eval(formula);
}

/**
 * EvaluateFormulaElements' per-element eval site (REQ-FORM-8). Capturable
 * locals of the v1 `.each` callback: params `d`, `elem` and the raw
 * `elem_formula` / `elem_formula_minvalue` data reads.
 */
export function evaluateFormulaElementFormula(
  parsedFormula: any,
  d: any,
  elem: any,
  elem_formula: any,
  elem_formula_minvalue: any,
): any {
  return eval(parsedFormula);
}

/**
 * EvaluateCalculationElements' per-element eval site (REQ-PRICE-4).
 * Capturable locals of the v1 `.each` callback: params `d`, `elem` and the
 * `elem_formula` / `elem_formula_minvalue` / `elem_current_value` /
 * `elem_decimals` / `element_name` data reads.
 */
export function evaluateCalculationElementFormula(
  parsedFormula: any,
  d: any,
  elem: any,
  elem_formula: any,
  elem_formula_minvalue: any,
  elem_current_value: any,
  elem_decimals: any,
  element_name: any,
): any {
  return eval(parsedFormula);
}
